import React, { useState } from 'react'
import Link from 'next/link'
import { useTrans, useTealium, useIsMobile } from '@next-nx/hooks'
import { MenuItemType } from '@next-nx/utils/types'
import { ArrowRight } from '@next-nx/shared-ui/icons'
import { WContainer, Modal, Button } from '@next-nx/shared-ui/atoms'
import { CategoriaCard } from '@next-nx/shared-ui/molecules'
import styles from './TrovaSoluzioneHome.module.sass'
import { getTimestamp, getAnchorRel } from '@next-nx/utils'

interface Props {
  item?: MenuItemType | undefined
  className?: string
}

const TrovaSoluzioneHome = (props: Props) => {
  const { item = undefined, className = '' } = props

  const { linkDataLayer } = useTealium()
  const t = useTrans()
  const isMobile = useIsMobile()
  const [selectedOption, setSelectedOption] = useState<MenuItemType | null>(null)
  const content = item
    ? item.children?.edges?.filter((x) => x.node.key === 'content')?.[0]?.node
    : null

  const handleTracking = (action_type: string = '', cta?: string, sezione?: string) => {
    const action_timestamp = getTimestamp()
    let dlObj = {}
    action_type = action_type.toLowerCase() === 'contattaci' ? 'flotte_aziendali' : action_type

    switch (sezione) {
      case 'soluzione_adatta':
        dlObj = {
          virtual: 'action',
          action_name: 'navigazione',
          action_tech: 'click',
          action_type: `soluzioni_per_${action_type
            .replace(/\/|\s+/g, '_')
            .replace(/\./g, '')
            .toLowerCase()}`,
          action_timestamp,
          action_effect: 'interazione_componente_soluzioni_adatte',
        }
        break
      default:
        dlObj = {
          virtual: 'action',
          action_name: 'popup_cataloghi',
          action_tech: 'click',
          action_type: action_type
            .replace(/\/|\s+/g, '_')
            .replace(/\./g, '')
            .toLowerCase(),
          action_timestamp,
          action_effect: 'navigazione_generale',
          cta: cta?.replace(/ /g, '_').toLowerCase(),
        }
        break
    }
    linkDataLayer(dlObj)
  }

  return item ? (
    <>
      <div className={`${styles.root} ${className}`}>
        <WContainer>
          <div className={styles.layout}>
            <div className={styles.content}>
              {!!item.name && <p className={styles.title}>{item.name}</p>}
              {!!item.description && (
                <div
                  className={styles.subtitle}
                  dangerouslySetInnerHTML={{ __html: item.description }}
                />
              )}
            </div>
            <div className={styles.links}>
              {!!content && !!content.children?.edges?.length && (
                <div className={styles.list}>
                  {content.children.edges
                    .map((x) => x.node)
                    .map((x, i) => (
                      <CategoriaCard
                        iconStyleName={x.style}
                        itemText={x.name}
                        itemUrl={x.url}
                        animationVariant={i % 2 === 0 ? 'se' : 'nw'}
                        key={x.pk}
                        variant="soluzione"
                        onClick={() => {
                          setSelectedOption(x), handleTracking(x.name, '', 'soluzione_adatta')
                        }}
                        className={x.name?.toHotjarFormat('shortcut_hp_{0}', isMobile)}
                      />
                    ))}
                </div>
              )}
              {!!content && !!content.ctaLabel && !!content.url && (
                <div className={styles.ctaBox}>
                  {!!content.description && (
                    <div
                      className={styles.ctaText}
                      dangerouslySetInnerHTML={{ __html: content.description }}
                    />
                  )}
                  <Link href={item.url || ''}>
                    <a
                      className={styles.ctaLink}
                      rel={getAnchorRel(item.url || '')}
                      onClick={() => handleTracking(content.ctaLabel, '', 'soluzione_adatta')}
                    >
                      {content.ctaLabel}
                    </a>
                  </Link>
                  <span className={styles.ctaArrow}>
                    <ArrowRight />
                  </span>
                </div>
              )}
            </div>
          </div>
        </WContainer>
      </div>
      <Modal
        open={!!selectedOption}
        setOpen={() => setSelectedOption(null)}
        title={t('Scopri le soluzioni per {0}', [selectedOption?.name || ''])}
        description={t('Consulta il nostro catalogo per trovare il veicolo adatto a te.')}
        closeButton
        className={styles.modal}
        sezione={selectedOption?.name}
        handleTracking={handleTracking}
      >
        <div className={styles.modalList}>
          {selectedOption?.children?.edges
            ?.map((x) => x.node)
            .map((x, i) => (
              <CategoriaCard
                iconStyleName={x.style}
                itemText={x.name}
                itemUrl={x.url ? x.url : ''}
                animationVariant={i % 2 === 0 ? 'se' : 'nw'}
                key={x.pk}
                sezione={selectedOption?.name}
                handleTracking={handleTracking}
              />
            ))}
        </div>
        <div className={styles.modalBottom}>
          <Button
            href={'/contattaci'}
            label={t('Hai delle esigenze particolari? Contattaci')}
            variant="ghost"
            icon={<ArrowRight />}
            iconPos="right"
            onClick={() => handleTracking(selectedOption?.name ?? '', 'contattaci')}
          />
        </div>
      </Modal>
    </>
  ) : (
    <></>
  )
}

export default TrovaSoluzioneHome
